import { useState, useCallback, useEffect, useRef, useContext } from 'react';
import { getAllPorts } from '../../../redux/actions/rfqManagementApi';
import { useDispatch } from 'react-redux';
import AuthContext from '../../../store/AuthContext';

const usePortAsyncTypeahead = () => {
    const [allPorts, setAllPorts] = useState([]); // All fetched ports
    const [options, setOptions] = useState([]); // Displayed ports
    const [isLoading, setIsLoading] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const isLoadingRef = useRef(false); // Prevent multiple simultaneous requests
    const dispatch = useDispatch();
    const ctx = useContext(AuthContext);

    // Fetch ports from API
    const fetchPorts = useCallback(async (cursor, query, append = false) => {
        // Prevent multiple simultaneous requests
        if (isLoadingRef.current) {
            return;
        }
        
        isLoadingRef.current = true;
        setIsLoading(true);
        
        try {
            const params = query ? { cursor, port_name: query } : { cursor };
            const req = { params, isLoadMore: append };
            
            const response = await Promise.resolve(dispatch(getAllPorts(req)));
            
            // Handle Redux thunk response structure
            if (response && response.payload) {
                const portsData = response.payload.payload || [];
                const nextCursorValue = response.payload.next_cursor;
                const hasNextValue = response.payload.has_next === true;
                
                if (portsData.length > 0) {
                    if (append) {
                        // Append to existing ports
                        setAllPorts(prev => {
                            const updatedPorts = [...prev, ...portsData];
                            return updatedPorts;
                        });
                    } else {
                        // Replace ports (new search or initial load)
                        setAllPorts(portsData);
                    }
                    
                    // Update pagination state
                    setNextCursor(nextCursorValue);
                    setHasNext(hasNextValue);
                } else {
                    // No more data
                    setHasNext(false);
                    setNextCursor(null);
                }
            } else {
                setHasNext(false);
            }
        } catch (error) {
            setHasNext(false);
            setNextCursor(null);
        } finally {
            setIsLoading(false);
            isLoadingRef.current = false;
        }
    }, [dispatch]);

    // Update options whenever allPorts changes
    useEffect(() => {
        const formattedOptions = allPorts.map(p => ({ 
            ...p, 
            name: p.name, 
            ulid: p.ulid, 
            label: p.name 
        }));
        setOptions(formattedOptions);
    }, [allPorts]);

    // Load more ports - only call when explicitly requested
    const loadMore = useCallback(() => {
        if (!isLoadingRef.current && hasNext && nextCursor !== null && nextCursor !== undefined) {
            fetchPorts(nextCursor, searchQuery, true);
        }
    }, [hasNext, nextCursor, searchQuery, fetchPorts]);

    // Search ports
    const search = useCallback((query) => {
        setSearchQuery(query);
        fetchPorts(0, query, false);
    }, [fetchPorts]);

    // Initial load: fetch first batch once Amplify session is ready
    useEffect(() => {
        if (!ctx.isSessionReady) return;
        fetchPorts(0, '', false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.isSessionReady]);

    return {
        options,
        isLoading,
        loadMore,
        search,
        hasNext,
    };
};

export default usePortAsyncTypeahead;
